import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
  Tab,
  Tabs,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Swal from "sweetalert2";

const redButton = {
  backgroundColor: "#d32f2f",
  color: "#fff !important",
  "&:hover": { backgroundColor: "#b71c1c" },
};

const whiteButtonText = {
  color: "#fff !important",
  "& .MuiButton-startIcon": { color: "#fff !important" },
};

const approveButton = {
  backgroundColor: "#2e7d32 !important",
  color: "#fff !important",
  "& .MuiButton-startIcon": { color: "#fff !important" },
  "&:hover": { backgroundColor: "#1b5e20 !important" },
};

export default function AttendanceFineRangeApprove() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");
  const clientId = user?.client_id,
    collegeId = user?.college_id,
    userId = user?.user_id;
  const [rows, setRows] = useState([]),
    [loading, setLoading] = useState(false),
    [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const swalOnTop = (options) => {
    const originalDidOpen = options.didOpen;

    return Swal.fire({
      target: document.body,
      ...options,
      didOpen: (popup) => {
        Swal.getContainer()?.style.setProperty("z-index", "20000", "important");

        originalDidOpen?.(popup);
      },
    });
  };

  const showMessage = (text, severity = "success") =>
    swalOnTop({
      title:
        severity === "error"
          ? "Unable to complete action"
          : severity === "warning"
          ? "Warning"
          : "Success",
      text,
      icon: severity,
      confirmButtonColor: severity === "error" ? "#d32f2f" : "#1976d2",
    });
  const fetchRows = useCallback(async () => {
    if (!clientId || !collegeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/attendance-fine-ranges/${clientId}/${collegeId}/approvals`
      );
      setRows(data?.data || []);
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Unable to load attendance fine approvals.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, clientId, collegeId]);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);
  const approve = async (row) => {
    const confirmed = await swalOnTop({
      title: "Approve attendance fine configuration?",
      text: "All pending ranges in this configuration will be approved.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2e7d32",
      cancelButtonColor: "#d32f2f",
    });
    if (!confirmed.isConfirmed) return;
    try {
      await axios.patch(
        `${API_BASE_URL}/api/attendance-fine-ranges/${clientId}/${collegeId}/approve`,
        { config_scope: row.config_scope, acyear_id: row.acyear_id, user_id: userId }
      );
      await fetchRows();
      setSelected(null);
      showMessage("Attendance fine configuration approved successfully.");
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to approve configuration.", "error");
    }
  };
  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 75,
        valueGetter: ({ api, id }) => api.getRowIndexRelativeToVisibleRows(id) + 1,
      },
      { field: "config_scope", headerName: "Configuration Scope", minWidth: 190, flex: 1 },
      { field: "academic_year", headerName: "Academic Year", minWidth: 160, flex: 0.8 },
      { field: "number_of_ranges", headerName: "Number of Fine Ranges", minWidth: 190, flex: 0.8 },
      {
        field: "actions",
        headerName: "Action",
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Button
            variant="contained"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => setSelected(row)}
            sx={whiteButtonText}
          >
            {row.workflow_status === "PENDING" ? "View / Approve" : "View"}
          </Button>
        ),
      },
    ],
    []
  );
  const renderGrid = (title, data) => (
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden", p: 1 }}>
      <Typography variant="h5" sx={{ p: 2 }}>
        {title} ({data.length})
      </Typography>

      <Box sx={{ height: 400, width: "100%", minWidth: 0 }}>
        <DataGrid
          rows={data.map((row, index) => ({
            ...row,
            id: row.id ?? `${row.config_scope}-${row.acyear_id}-${index}`,
          }))}
          columns={columns}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize);
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          pagination
          disableSelectionOnClick
        />
      </Box>
    </Paper>
  );
  const pending = rows.filter((row) => row.workflow_status === "PENDING"),
    approved = rows.filter((row) => row.workflow_status === "APPROVED");
  return (
    <Box p={{ xs: 1, sm: 2 }} sx={{ minWidth: 0 }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            APPROVE ATTENDANCE FINE RANGE
          </Typography>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, minWidth: 0, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => {
            setActiveTab(newValue);
            setPage(0);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 64,
            mb: 2,
            borderBottom: "1px solid #e0e0e0",

            "& .MuiTabs-indicator": {
              height: 4,
              backgroundColor: "#455a64",
              borderRadius: "4px 4px 0 0",
            },

            "& .MuiTab-root": {
              minHeight: 60,
              minWidth: { xs: 170, sm: 250 },
              px: 2,
              mr: 1,
              borderRadius: "8px 8px 0 0",
              fontSize: { xs: 12, sm: 14 },
              fontWeight: 700,
              textTransform: "none",
              opacity: 1,
            },

            /* Pending - light orange */
            "& .MuiTab-root:nth-of-type(1)": {
              backgroundColor: "#FFF3E0",
              color: "#9A5300",
              "&:hover": { backgroundColor: "#FFE0B2" },
            },

            /* Approved - light green */
            "& .MuiTab-root:nth-of-type(2)": {
              backgroundColor: "#E8F5E9",
              color: "#1B5E20",
              "&:hover": { backgroundColor: "#C8E6C9" },
            },

            "& .MuiTab-root.Mui-selected": {
              fontWeight: 800,
              boxShadow: "inset 0 -4px 0 #455a64",
            },
          }}
        >
          <Tab label={`Pending (${pending.length})`} />
          <Tab label={`Approved (${approved.length})`} />
        </Tabs>

        {activeTab === 0 && renderGrid("PENDING ATTENDANCE FINE CONFIGURATION", pending)}

        {activeTab === 1 && renderGrid("APPROVED ATTENDANCE FINE CONFIGURATION", approved)}
      </Paper>
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Attendance Fine Configuration
          <IconButton
            onClick={() => setSelected(null)}
            sx={{ position: "absolute", right: 8, top: 8, color: "#d32f2f" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography mb={1}>
            <strong>Scope:</strong> {selected?.config_scope || "-"}
            &nbsp;&nbsp;
            <strong>Academic Year:</strong>{" "}
            {selected?.config_scope === "COMMON" ? "Common" : selected?.academic_year || "-"}
          </Typography>
          <Box sx={{ height: 300, width: "100%" }}>
            <DataGrid
              rows={(selected?.ranges || []).map((range) => ({
                ...range,
                id: range.fine_range_id,
              }))}
              columns={[
                { field: "at_range", headerName: "Attendance(and above) Range", flex: 1 },
                { field: "fine_amount", headerName: "Fine Amount Per Percentage", flex: 1 },
              ]}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableSelectionOnClick
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {selected?.workflow_status === "PENDING" && (
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={() => approve(selected)}
              sx={approveButton}
            >
              Approve
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<CloseIcon />}
            onClick={() => setSelected(null)}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: 700,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
